import {
  MailOutlined,
  MergeCellsOutlined,
  MessageOutlined,
  UnorderedListOutlined
} from '@ant-design/icons'
import {
  type Dropdown,
  type LeadsContactsListData,
  type PaginatedDropdownList,
  type PaginatedReturnContactListReadable,
  type ReturnContactReadable,
  type UsersListListData,
  leadsContactsListInfiniteOptions,
  usersListListInfiniteOptions
} from '@crm/api'
import { InfiniteSelect, MergeLeadModal } from '@crm/components'
import { MergeOpportunityModal } from '@crm/components/InfoTabs/MergeOpportunityModal'
import LogsPanelContext from '@crm/contexts/LogsPanelContext'
import { SendClientForm } from '@crm/features/leads/components'
import { LogType } from '@crm/types'
import { defaultFiltersMultipleValues, userTransformResults } from '@crm/utils'
import { useQueryUpdate } from '@shared/hooks'
import { useLocales } from '@shared/locales'
import { Button, Flex, Select, Space } from 'antd'
import { useContext, useState } from 'react'
import { useParams } from 'react-router-dom'

import { useStyle } from './styles'

type DefaultStateToProps = {
  visibleMergeModal: boolean
  visibleSendModal: boolean
  visibleTrackingModal: boolean
}

const defaultState = {
  visibleMergeModal: false,
  visibleSendModal: false,
  visibleTrackingModal: false
} as const

export const ActivityFilters = () => {
  const { t } = useLocales()
  const { id, opportunityId } = useParams()
  const { styles } = useStyle()
  const { params, updateQuery } = useQueryUpdate()

  const { isOpenSendMail, isOpenCreateNote, toggleSendMail, toggleCreateNote } =
    useContext(LogsPanelContext)

  const [state, setState] = useState<DefaultStateToProps>(defaultState)

  const onSendSubmit = () =>
    setState((prev) => ({ ...prev, visibleSendModal: false }))

  const ownerValue = defaultFiltersMultipleValues(params?.created_by)
  const contactValue = defaultFiltersMultipleValues(params?.contact, true)

  return (
    <>
      <Flex justify="space-between" gap={10} wrap className={styles.filters}>
        <Space wrap size={10}>
          <Select
            placeholder={t('logs.type')}
            value={
              Array.isArray(params.type)
                ? params.type
                : params.type && [params.type]
            }
            mode="multiple"
            className="select-min-width"
            options={Object.values(LogType).map((item) => ({
              label: t(`logs.${item}`),
              value: item
            }))}
            onChange={(type) => updateQuery({ type })}
          />

          <InfiniteSelect<UsersListListData, PaginatedDropdownList, Dropdown>
            value={ownerValue}
            className="select-min-width"
            popupMatchSelectWidth
            placeholder={t('logs.users')}
            getQueryFunction={usersListListInfiniteOptions}
            onChange={(created_by) => updateQuery({ created_by })}
            baseOptions={{
              query: {
                lead: Number(id),
                preference: params?.created_by
              }
            }}
          />

          <InfiniteSelect<
            LeadsContactsListData,
            PaginatedReturnContactListReadable,
            ReturnContactReadable
          >
            value={contactValue}
            className="select-min-width"
            popupMatchSelectWidth
            placeholder={t('logs.all_contacts')}
            getQueryFunction={leadsContactsListInfiniteOptions}
            onChange={(contact) => updateQuery({ contact })}
            getOptions={({ options }) => userTransformResults(options)}
            baseOptions={{
              path: {
                lead_id: String(id)
              },
              query: {
                preference: params?.contact
              }
            }}
          />
        </Space>

        <Space wrap size={10}>
          {/* Commented because back-end doesn't work properly */}
          {/* <Button
              icon={<EyeOutlined />}
              onClick={() => setState({ visibleTrackingModal: true })}
            >
              {t('activities.tracking')}
            </Button> */}

          <Button
            icon={<MergeCellsOutlined />}
            onClick={() =>
              setState((prev) => ({ ...prev, visibleMergeModal: true }))
            }
          >
            {t(
              opportunityId
                ? 'activities.merge_opportunity'
                : 'activities.merge_lead'
            )}
          </Button>

          <Button
            icon={<MailOutlined />}
            onClick={toggleSendMail}
            disabled={isOpenSendMail}
          >
            {t('activities.email')}
          </Button>

          <Button
            icon={<UnorderedListOutlined />}
            onClick={toggleCreateNote}
            disabled={isOpenCreateNote}
          >
            {t('activities.add_note')}
          </Button>

          <Button
            icon={<MessageOutlined />}
            onClick={() =>
              setState((prev) => ({ ...prev, visibleSendModal: true }))
            }
          >
            {t('activities.send')}
          </Button>
        </Space>
      </Flex>

      <SendClientForm
        open={state?.visibleSendModal}
        leads={[Number(id)]}
        onClose={() => setState(defaultState)}
        onSubmit={onSendSubmit}
      />

      {opportunityId ? (
        <MergeOpportunityModal
          open={state.visibleMergeModal}
          onCancel={() => setState(defaultState)}
        />
      ) : (
        <MergeLeadModal
          open={state.visibleMergeModal}
          onCancel={() => setState(defaultState)}
        />
      )}

      {/* Commented because back-end doesn't work properly */}
      {/* <TrackClient
        open={state?.visibleTrackingModal}
        clientId={Number(id)}
        onClose={() => setState(defaultState)}
      /> */}
    </>
  )
}
